import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { Anchor, Container } from 'components/base';
import contentApi from 'data/api/content';
import { couponSale, discountLabel, endsSoonLabel } from 'ecommerce/lib/coupon';

import Breadcrumb from '../breadcrumb';
import CatalogProductCard from '../custom/catalogProductCard';
import { ProductDetailLink } from '../pageLinks';
import PdpIcon from '../product/pdpIcon';
import { money } from '../../lib/landingPrice';
import PRODUCT_CATEGORIES from '../../specifications/categories';
import GiftIdea from './giftIdea';
import { HeroCollage, HeroFacts } from '../landing/hero';
import { GIFT_CARD_HREF, GIFT_FACTS } from './shared';
import { Responsive, SectionHead } from '../landing/shared';
import StickyBar from '../landing/stickyBar';

// Sections only the budget pages (/gifts-under-20, /gifts-under-50) render, see
// docs/gifts-page.md#budget-pages. Each page composes them with its own copy.

export const ALL_GIFTS_ID = 'all-gifts';

// Links between the sibling budget pages. The current page is not a link.
const SWITCH = [
  { href: '/gifts-under-20', label: 'Under $20' },
  { href: '/gifts-under-50', label: 'Under $50' },
  { href: GIFT_CARD_HREF, label: 'Gift Cards' },
];

// Grid card element id, the recipient rings jump to it.
export const cardId = (productId) => `gift-${productId}`;

export const BudgetHero = forwardRef(
  ({ category, breadcrumb, lead, giftCount, images, tag, track }, ref) => (
    <section ref={ref} className="lnd-hero">
      <Container className="lnd-hero-inner">
        <div className="lnd-hero-copy">
          <Breadcrumb items={breadcrumb} />
          <nav aria-label="Gifts by budget" className="gft-switch">
            {SWITCH.map((item) =>
              item.href === category.url ? (
                <span key={item.href} className="gft-switch-item active" aria-current="page">
                  {item.label}
                </span>
              ) : (
                <Anchor
                  key={item.href}
                  href={item.href}
                  className="gft-switch-item"
                  onClick={track('switch', 'hero', item.href)}
                >
                  {item.label}
                </Anchor>
              ),
            )}
          </nav>
          <h1 className="lnd-h1 gft-h1--budget">{category.heroTitle ?? category.name}</h1>
          <p className="lnd-lead">
            <Responsive desktop={lead.desktop} phone={lead.phone} />
          </p>
          <div className="lnd-hero-actions">
            <a href={`#${ALL_GIFTS_ID}`} className="lnd-btn" onClick={track('all_gifts', 'hero')}>
              {`See all ${giftCount} gifts`}
              <PdpIcon name="arrowDown" size={16} />
            </a>
            <Anchor
              href={GIFT_CARD_HREF}
              className="lnd-btn lnd-btn--outline"
              onClick={track('gift_card', 'hero')}
            >
              <PdpIcon name="mail" size={16} className="lnd-desk" />
              Send a Gift Card
              <PdpIcon name="mail" size={16} className="lnd-phone" />
            </Anchor>
          </div>
          <HeroFacts facts={GIFT_FACTS} />
        </div>
        <HeroCollage images={images} tag={tag} />
      </Container>
    </section>
  ),
);

BudgetHero.displayName = 'BudgetHero';

BudgetHero.propTypes = {
  category: PropTypes.object.isRequired,
  breadcrumb: PropTypes.arrayOf(PropTypes.object).isRequired,
  lead: PropTypes.shape({ desktop: PropTypes.string.isRequired, phone: PropTypes.string })
    .isRequired,
  giftCount: PropTypes.number.isRequired,
  images: HeroCollage.propTypes.images,
  tag: HeroCollage.propTypes.tag,
  track: PropTypes.func.isRequired,
};

const GRID_SIZES = {
  3: '(min-width: 992px) 384px, (min-width: 576px) 50vw, 50vw',
  4: '(min-width: 992px) 282px, (min-width: 576px) 50vw, 50vw',
};

// Every product of the budget category in the spec order, as catalog product cards. The badge
// is the page's use case for the product, not the content `badge`.
export const BudgetGrid = forwardRef(
  ({ eyebrow, title, products, columns, previewSummary, track }, ref) => (
    <section id={ALL_GIFTS_ID} className="lnd-section gft-all">
      <Container>
        <SectionHead
          eyebrow={eyebrow}
          title={title}
          aside="Prices are starting prices. Pick sizes and finishes on the next page."
        />
        <ul className={`gft-grid gft-grid--${columns}`}>
          {products.map(({ product, meta, badge }) => (
            <li key={product.id} id={cardId(product.id)}>
              <ProductDetailLink productId={product.id}>
                <CatalogProductCard
                  product={product}
                  meta={meta}
                  badge={badge}
                  sale={couponSale(previewSummary, product)}
                  ctaLabel="Make this gift"
                  sizes={GRID_SIZES[columns]}
                  onClick={track('product', 'all_gifts', product.id)}
                />
              </ProductDetailLink>
            </li>
          ))}
        </ul>
      </Container>
      <div ref={ref} className="gft-sentinel" />
    </section>
  ),
);

BudgetGrid.displayName = 'BudgetGrid';

BudgetGrid.propTypes = {
  eyebrow: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      product: PropTypes.object.isRequired,
      meta: PropTypes.string,
      badge: PropTypes.string,
    }),
  ).isRequired,
  columns: PropTypes.oneOf([3, 4]).isRequired,
  previewSummary: PropTypes.object,
  track: PropTypes.func.isRequired,
};

// Three gift ideas side by side (one row, phones stack them).
export const UseCases = ({ eyebrow, title, aside, ideas, track }) => (
  <section className="lnd-section gft-ideas">
    <Container>
      <SectionHead eyebrow={eyebrow} title={title} aside={aside} className="gft-head--ideas" />
      <div className="gft-idea-row gft-idea-row--three">
        {ideas.map((idea) => (
          <GiftIdea key={idea.id} idea={idea} onClick={track('idea', 'use_cases', idea.id)} />
        ))}
      </div>
    </Container>
  </section>
);

UseCases.propTypes = {
  eyebrow: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  aside: PropTypes.string,
  ideas: PropTypes.arrayOf(PropTypes.object).isRequired,
  track: PropTypes.func.isRequired,
};

// Phone sticky bar: the grid until it has been scrolled past, then the Gift Card fallback.
export const BudgetStickyBar = ({ visible, pastGrid, products, previewSummary, track }) => {
  if (pastGrid) {
    return (
      <StickyBar
        visible={visible}
        title="Short on time?"
        meta="Gift Card, emailed in minutes"
        href={GIFT_CARD_HREF}
        label="Send a Gift Card"
        icon="mail"
        onClick={track('gift_card', 'sticky')}
      />
    );
  }

  const prices = products.map(({ product }) => {
    const sale = couponSale(previewSummary, product);
    return { amount: sale?.discountedPriceFrom ?? product.prices[0], currency: product.currency };
  });
  const cheapest = prices.length
    ? prices.reduce((min, price) => (price.amount < min.amount ? price : min))
    : null;
  const covered = products.some(({ product }) => couponSale(previewSummary, product)?.badge);
  const coupon =
    covered &&
    [`${discountLabel(previewSummary)} with ${previewSummary.code}`, endsSoonLabel(previewSummary)]
      .filter(Boolean)
      .join(' · ');

  return (
    <StickyBar
      visible={visible}
      title={cheapest ? `Gifts from ${money(cheapest.amount, cheapest.currency)}` : 'Gifts'}
      meta={coupon || `${products.length} gifts · gift note at checkout`}
      sale={Boolean(coupon)}
      href={`#${ALL_GIFTS_ID}`}
      label="See all gifts"
      icon="arrowDown"
      onClick={track('all_gifts', 'sticky')}
    />
  );
};

BudgetStickyBar.propTypes = {
  visible: PropTypes.bool.isRequired,
  pastGrid: PropTypes.bool.isRequired,
  products: PropTypes.arrayOf(PropTypes.shape({ product: PropTypes.object.isRequired })).isRequired,
  previewSummary: PropTypes.object,
  track: PropTypes.func.isRequired,
};

// Page props of a budget page: its content entry and the card meta line of every product in
// the category (content `cardMeta`), without the rest of the product content.
export const loadBudgetContent = async (categoryId) => {
  const ids = PRODUCT_CATEGORIES.find((category) => category.id === categoryId).data.flatMap(
    (section) => section.products,
  );
  const [content, productContent] = await Promise.all([
    contentApi.category(categoryId),
    contentApi.products(ids),
  ]);
  return {
    content,
    cardMeta: Object.fromEntries(productContent.map((entry) => [entry.id, entry.cardMeta ?? null])),
  };
};
